<!-- BulkRemoveMemberAccountForm.vue -->
<!-- Revoga vínculos de membros do titular logado em lote.             -->
<!-- Revogados aparecem pré-marcados e desabilitados.                  -->
<!-- Ativos/Inativos aparecem desmarcados e habilitados para seleção.  -->
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import * as managedAccountService from "@/services/managedAccountService";
import api from "@/services/api";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";

import ThePagination from "@/components/Generics/ThePagination.vue";

import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "done"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// ── Estado ────────────────────────────────────────────────────────────────────
const isLoading = ref(false);
const isSubmitting = ref(false);

// Lista completa de relacionamentos do titular (todos os status)
const allRelationships = ref([]);
const totalCount = ref(0);
const totalPages = ref(1);
const currentPage = ref(1);
const pageSize = ref(10);
const searchTerm = ref("");

// IDs de relacionamentos selecionados para revogar
const selectedRelationshipIds = ref([]);

// ── Toast ─────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("error");
const toastTimeoutId = ref(null);

// Ao revogar com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("done") desmontar o componente.
const hideBackdropForSuccess = ref(false);

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveRelationshipPhotoPath(rel) {
  if (!rel) return null;

  return (
    rel.managedUserPhotoPath ||
    rel.managedUserPhotoUrl ||
    rel.userPhotoPath ||
    rel.userPhotoUrl ||
    rel.photoPath ||
    rel.photoUrl ||
    rel.managedUser?.photoPath ||
    rel.managedUser?.photoUrl ||
    rel.photo_path ||
    rel.user_photo_path ||
    rel.managed_user_photo_path ||
    null
  );
}

function showErrorToast(message, duration = 3000) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, duration);
}

function showSuccessToast(message, duration = 1500) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, duration);
}

// ── Computed ──────────────────────────────────────────────────────────────────
const counterText = computed(() => {
  const selected = selectedRelationshipIds.value.length;
  return `${selected} selecionados / ${totalCount.value} vínculos`;
});

// ── Helpers de status ─────────────────────────────────────────────────────────
function isRevoked(rel) {
  return rel.status === "Revoked";
}

function isChecked(rel) {
  return (
    isRevoked(rel) ||
    selectedRelationshipIds.value.includes(rel.managedAccountRelationshipId)
  );
}

function isDisabled(rel) {
  return isRevoked(rel);
}

function statusLabel(status) {
  const map = { Active: "Ativo", Inactive: "Inativo", Revoked: "Revogado" };
  return map[status] ?? status ?? "—";
}

function statusClass(status) {
  if (status === "Active") return "status-pill status-active";
  if (status === "Inactive") return "status-pill status-inactive";
  if (status === "Revoked") return "status-pill status-revoked";
  return "status-pill";
}

function toggleRelationship(rel) {
  if (isDisabled(rel)) return;
  const id = rel.managedAccountRelationshipId;
  const idx = selectedRelationshipIds.value.indexOf(id);
  if (idx > -1) {
    selectedRelationshipIds.value.splice(idx, 1);
  } else {
    selectedRelationshipIds.value.push(id);
  }
}

// ── Fetch ─────────────────────────────────────────────────────────────────────
async function fetchRelationships() {
  isLoading.value = true;
  try {
    const response = await managedAccountService.fetchPaged(
      currentPage.value,
      pageSize.value,
      searchTerm.value
    );
    allRelationships.value = response.data.data ?? [];
    totalCount.value = response.data.totalCount ?? 0;
    totalPages.value = response.data.totalPages ?? 1;
  } catch (e) {
    showErrorToast("Erro ao carregar membros.");
  } finally {
    isLoading.value = false;
  }
}

// ── Envio ─────────────────────────────────────────────────────────────────────
async function handleSubmit() {
  if (isSubmitting.value) return;

  if (selectedRelationshipIds.value.length === 0) {
    showErrorToast("Selecione pelo menos um vínculo para revogar.");
    return;
  }

  isSubmitting.value = true;
  try {
    await managedAccountService.bulkRevokeByIds({
      relationshipIds: [...selectedRelationshipIds.value],
    });

    showSuccessToast(
      `${selectedRelationshipIds.value.length} vínculo(s) revogado(s) com sucesso.`,
      1500
    );
    // Esconde o painel do modal já — o toast segue visível sozinho até o
    // emit("done") desmontar o componente após o tempo do toast.
    hideBackdropForSuccess.value = true;
    setTimeout(() => emit("done"), 1500);
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      "Erro ao revogar vínculos.";
    showErrorToast(msg, 3000);
    isSubmitting.value = false;
  }
}

// ── Ciclo de vida ─────────────────────────────────────────────────────────────
onMounted(() => fetchRelationships());

onBeforeUnmount(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

watch(currentPage, fetchRelationships);

let searchDebounce = null;
watch(searchTerm, () => {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    currentPage.value = 1;
    fetchRelationships();
  }, 400);
});
</script>

<template>
  <div v-show="!hideBackdropForSuccess" class="modal-backdrop">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <!-- ── Cabeçalho ────────────────────────────────────────────────────── -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Apagar / Revogar Vínculos em Lote</h2>
        <button
          type="button"
          @click="emit('close')"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <!-- ── Corpo ─────────────────────────────────────────────────────────── -->
      <div class="modal-body">
        <p class="modal-description">
          Selecione os vínculos que deseja revogar. Vínculos já revogados
          aparecem marcados e não podem ser alterados.
        </p>

        <!-- Aviso de irreversibilidade -->
        <div class="warning-box">
          <i class="fas fa-exclamation-triangle"></i>
          <span
            >A revogação é permanente. O vínculo não poderá ser reativado.</span
          >
        </div>

        <!-- Lista de membros -->
        <div class="list-container">
          <div class="list-title">
            <h3>Membros</h3>
            <span class="counter-tag">{{ counterText }}</span>
          </div>

          <!-- Busca inline -->
          <div class="search-wrapper">
            <div class="search-icon-wrap">
              <svg class="search-icon" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <input
              v-model="searchTerm"
              type="text"
              class="search-input"
              placeholder="Buscar por nome, username ou e-mail..."
            />
          </div>

          <div class="list-scroll">
            <!-- Carregando -->
            <div v-if="isLoading" class="list-loading-state">
              <i class="fas fa-spinner fa-spin"></i> Carregando...
            </div>

            <!-- Vazio -->
            <div
              v-else-if="allRelationships.length === 0"
              class="list-empty-state"
            >
              Nenhum membro encontrado.
            </div>

            <!-- Itens -->
            <div
              v-else
              v-for="rel in allRelationships"
              :key="rel.managedAccountRelationshipId"
              class="list-item"
              :class="{ 'list-item--disabled': isDisabled(rel) }"
              @click="toggleRelationship(rel)"
            >
              <input
                type="checkbox"
                :checked="isChecked(rel)"
                :disabled="isDisabled(rel)"
                class="checkbox-style"
                :class="{ 'checkbox-revoked': isRevoked(rel) }"
                @click.stop
                @change="toggleRelationship(rel)"
              />
              <div class="item-info">
                <div class="item-main">
                  <span class="member-avatar">
                    <span
                      v-if="getAvatarUrl(resolveRelationshipPhotoPath(rel))"
                      class="member-avatar-img"
                      :style="{
                        backgroundImage: `url('${getAvatarUrl(
                          resolveRelationshipPhotoPath(rel)
                        )}')`,
                      }"
                    ></span>

                    <i v-else class="fas fa-user member-avatar-icon"></i>
                  </span>

                  <span class="item-name">
                    {{ toTitleCase(rel.fullName) || rel.userName }}
                  </span>

                  <span :class="statusClass(rel.status)">
                    {{ statusLabel(rel.status) }}
                  </span>
                </div>
                <div class="item-meta">
                  <span class="item-email">{{ rel.email }}</span>
                  <span class="item-date">
                    vinculado em {{ formatDateTimeForDisplay(rel.createdAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <ThePagination
            class="pagination-tight border-t"
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="totalCount"
            :total-pages="totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>

      <!-- ── Rodapé ────────────────────────────────────────────────────────── -->
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="isSubmitting"
          @click="emit('close')"
        >
          Cancelar
        </button>

        <button
          type="button"
          class="btn btn-danger"
          :disabled="isSubmitting || selectedRelationshipIds.length === 0"
          @click="handleSubmit"
        >
          <svg
            v-if="isSubmitting"
            class="w-5 h-5 mr-2 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          {{
            isSubmitting
              ? "Revogando..."
              : `Revogar Selecionados (${selectedRelationshipIds.length})`
          }}
        </button>
      </div>
    </div>
  </div>

  <!-- ── Toast ─────────────────────────────────────────────────────────────── -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-content">
            <p class="toast-title">
              {{
                toastType === "error" ? "Não foi possível concluir" : "Sucesso!"
              }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close"
            @click="showToast = false"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-width: 580px;
  max-height: calc(100vh - 14px - 1.5rem);
}

.modal-description {
  font-size: 0.88rem;
  color: var(--text-muted);
  margin: 0 0 0.75rem;
  line-height: 1.5;
}

/* ── Aviso ──────────────────────────────────────────────────────────────── */
.warning-box {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.82rem;
  color: #9a3412;
  line-height: 1.4;
}

.warning-box i {
  flex-shrink: 0;
  margin-top: 0.05rem;
}

/* ── Lista ──────────────────────────────────────────────────────────────── */
.list-container {
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  overflow: hidden;
  background: var(--surface);
  display: flex;
  flex-direction: column;
}

.list-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.45rem 0.875rem;
  background-color: var(--bg-header);
  font-weight: 600;
  font-size: 0.92rem;
  border-bottom: 1px solid var(--border-color);
}

.counter-tag {
  font-size: 0.72rem;
  background-color: var(--neutral-200);
  color: var(--text-light);
  padding: 0.2rem 0.65rem;
  border-radius: 9999px;
  font-weight: 600;
}

/* ── Busca inline ────────────────────────────────────────────────────────── */
.search-wrapper {
  position: relative;
  padding: 0.4rem 0.5rem;
  background: var(--surface);
  border-bottom: 1px solid var(--surface-3);
}

.search-icon-wrap {
  position: absolute;
  top: 50%;
  left: 0.9rem;
  transform: translateY(-50%);
  pointer-events: none;
}

.search-icon {
  width: 0.95rem;
}

.list-scroll {
  max-height: 340px;
  overflow-y: auto;
  padding: 0.4rem;
}

.list-loading-state,
.list-empty-state {
  text-align: center;
  padding: 2rem;
  color: var(--text-subtle);
  font-size: 0.85rem;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 0.2rem;
}

.list-item:hover:not(.list-item--disabled) {
  background-color: var(--surface-2);
}

.list-item--disabled {
  cursor: default;
  opacity: 0.7;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
  min-width: 0;
}

.item-main {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.item-name {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--text-dark);
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.item-email {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.item-date {
  font-size: 0.72rem;
  color: var(--text-subtle);
}

/* ── Status pills ────────────────────────────────────────────────────────── */
.status-pill {
  display: inline-block;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
}

.status-active {
  background: var(--success-bg);
  color: var(--success-text);
}

.status-inactive {
  background: var(--warning-bg);
  color: #854d0e;
}

.status-revoked {
  background: var(--danger-bg);
  color: var(--danger-text);
}

/* ── Paginação ──────────────────────────────────────────────────────────── */
.pagination-tight {
  padding: 6px !important;
  margin: 6px !important;
}

.checkbox-style {
  width: 1.05rem;
}

/* Revogados: checkbox marcado em vermelho para diferenciar */
.checkbox-revoked:checked {
  background-color: #f87171;
  border-color: #f87171;
}

.btn:hover:not(:disabled) {
  /* Apenas mudança de background-color; sem transform nem box-shadow */
}

.toast-title {
  margin-bottom: 0.2rem;
}

.toast-close:hover {
  /* Sem transform nem box-shadow */
}

/* ── Avatar do membro na lista ───────────────────────────────────────────── */
.member-avatar {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 26px;
}

.member-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.member-avatar-icon {
  font-size: 0.75rem;
  line-height: 1;
}
</style>